import React, { useState, useEffect, useRef } from 'react';
import { Button, Dropdown, Menu, DatePicker, Tooltip } from 'antd';
import { 
  CalendarOutlined, 
  LeftOutlined, 
  RightOutlined,
  DownOutlined,
  LoadingOutlined,
  FieldTimeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ordersService from '../services/ordersService';

const { RangePicker } = DatePicker;

const PICKER_POPUP_SELECTOR = '.golden-date-range-popup';
const PICKER_TITLE_SELECTOR = '.golden-date-range-popup [title]';

const stripPickerTitles = (root = document) => {
  root
    .querySelectorAll(PICKER_TITLE_SELECTOR)
    .forEach((element) => {
      element.removeAttribute('title');
    });
};

const DateRangeSelector = ({ 
  dateViewMode = 'day', // Default to day view
  currentDate,
  dateDisplayText,
  isCustomRange,
  onDateRangeChange,
  onCustomRangeChange,
  onRangeChange,
  onDataLoaded
}) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCustomRangePicker, setShowCustomRangePicker] = useState(false);
  const [rangePickerOpen, setRangePickerOpen] = useState(false);
  const [initializedDate, setInitializedDate] = useState(false);
  const rangePickerRef = useRef(null);
  const closePickerTimerRef = useRef(null);
  const dropdownContentRef = useRef(null);

  const closeCustomRangePicker = () => {
    if (closePickerTimerRef.current) {
      clearTimeout(closePickerTimerRef.current);
    }

    rangePickerRef.current?.blur?.();
    setRangePickerOpen(false);
    closePickerTimerRef.current = setTimeout(() => {
      setShowCustomRangePicker(false);
      setVisible(false);
      closePickerTimerRef.current = null;
    }, 0);
  };

  useEffect(() => {
    return () => {
      if (closePickerTimerRef.current) {
        clearTimeout(closePickerTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!rangePickerOpen) return;

    let animationFrame;

    const scheduleStrip = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      animationFrame = requestAnimationFrame(() => stripPickerTitles());
    };

    scheduleStrip();

    const stripEventTargetTitles = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const titleHolder = target?.closest?.('.golden-date-range-popup [title]');

      if (titleHolder) {
        titleHolder.removeAttribute('title');
      }

      scheduleStrip();
    };

    const observer = new MutationObserver(scheduleStrip);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['title'],
      childList: true,
      subtree: true
    });

    const interval = setInterval(stripPickerTitles, 100);
    document.addEventListener('pointerover', stripEventTargetTitles, true);
    document.addEventListener('mouseover', stripEventTargetTitles, true);
    document.addEventListener('focusin', stripEventTargetTitles, true);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      observer.disconnect();
      clearInterval(interval);
      document.removeEventListener('pointerover', stripEventTargetTitles, true);
      document.removeEventListener('mouseover', stripEventTargetTitles, true);
      document.removeEventListener('focusin', stripEventTargetTitles, true);
    };
  }, [rangePickerOpen]);

  useEffect(() => {
    if (!visible && !showCustomRangePicker && !rangePickerOpen) return;

    const closeOnOutsidePointer = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const clickedDropdown = dropdownContentRef.current?.contains(target);
      const clickedPickerPopup = target.closest(PICKER_POPUP_SELECTOR);

      if (clickedDropdown || clickedPickerPopup) {
        return;
      }

      closeCustomRangePicker();
    };

    document.addEventListener('pointerdown', closeOnOutsidePointer, true);
    document.addEventListener('mousedown', closeOnOutsidePointer, true);
    document.addEventListener('touchstart', closeOnOutsidePointer, true);

    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer, true);
      document.removeEventListener('mousedown', closeOnOutsidePointer, true);
      document.removeEventListener('touchstart', closeOnOutsidePointer, true);
    };
  }, [visible, showCustomRangePicker, rangePickerOpen]);
  
  // Initialize component with today's date if no currentDate is provided
  useEffect(() => {
    if (!initializedDate) {
      initializeWithToday();
      setInitializedDate(true);
    }
  }, []);
  
  // Initialize the component with today's date
  const initializeWithToday = async () => {
    const today = new Date();
    const dateRange = getDateRange('day', today);
    const displayText = formatDisplayDate(today);
    
    // Fetch data for today
    if (onDataLoaded) {
      await fetchDataForDateRange(dateRange);
    }
    
    // Update parent component with today's date
    if (typeof onDateRangeChange === 'function') {
      onDateRangeChange(dateRange, 'day', displayText, today);
    } else if (typeof onRangeChange === 'function') {
      // Fallback to onRangeChange if onDateRangeChange is not provided
      onRangeChange(dateRange, 'day', displayText, today);
    }
  };

  // Format a date to YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return dayjs().format('YYYY-MM-DD');
    return dayjs(date).format('YYYY-MM-DD');
  };
  
  // Format display date in consistent format
  const formatDisplayDate = (date) => {
    if (!date) return dayjs().format('DD MMM YYYY');
    return dayjs(date).format('DD MMM YYYY');
  };
  
  // Handle navigation between days/weeks/months
  const handleNavigation = async (direction) => {
    if (isCustomRange) return;
    
    let newDateObj;
    // If no currentDate is provided, start with today
    const currentDateObj = currentDate ? dayjs(currentDate) : dayjs(new Date());
    
    if (dateViewMode === 'day') {
      // Navigate by days
      newDateObj = direction === 'next' 
        ? currentDateObj.add(1, 'day').toDate()
        : currentDateObj.subtract(1, 'day').toDate();
    } else if (dateViewMode === 'week') {
      // Navigate by weeks
      newDateObj = direction === 'next' 
        ? currentDateObj.add(1, 'week').toDate()
        : currentDateObj.subtract(1, 'week').toDate();
    } else if (dateViewMode === 'month') {
      // Navigate by months
      newDateObj = direction === 'next' 
        ? currentDateObj.add(1, 'month').toDate()
        : currentDateObj.subtract(1, 'month').toDate();
    } else {
      newDateObj = currentDateObj.toDate();
    }
    
    // Calculate the date range based on the view mode
    const dateRange = getDateRange(dateViewMode, newDateObj);
    
    // Generate display text
    const displayText = getDisplayText(dateViewMode, newDateObj);
    
    // Fetch data from backend before updating parent component
    if (onDataLoaded) {
      await fetchDataForDateRange(dateRange);
    }
    
    // Call the parent's function to update the date
    if (typeof onDateRangeChange === 'function') {
      onDateRangeChange(dateRange, dateViewMode, displayText, newDateObj);
    } else if (typeof onRangeChange === 'function') {
      onRangeChange(dateRange, dateViewMode, displayText, newDateObj);
    }
  };
  
  // Function to fetch data from backend for a date range
  const fetchDataForDateRange = async (dateRange) => {
    try {
      setLoading(true);
      
      // Create params object for the API request
      const params = {};
      
      // If date range is a single day, use property_date
      if (dateRange[0] === dateRange[1]) {
        params.property_date = dateRange[0];
      } else {
        // Otherwise use start_date and end_date
        params.start_date = dateRange[0];
        params.end_date = dateRange[1];
      }
      
      // Include metrics calculation
      params.calculate_metrics = true;
      
      // Make the API call
      const response = await ordersService.getAllOrdersAdmin(params);
      
      // Call the callback with fetched data
      if (onDataLoaded && response) {
        onDataLoaded(response);
      }
      
    } catch (error) {
      console.error('Error fetching orders for date range:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate the date range for a given mode
  const getDateRange = (mode, date) => {
    const currentDay = dayjs(date || new Date());
    
    if (mode === 'day') {
      // For a single day, just return the same date for start and end
      const formattedDate = formatDate(currentDay);
      return [formattedDate, formattedDate];
    } 
    else if (mode === 'week') {
      // Calculate start of week (Sunday) and end of week (Saturday)
      const startOfWeek = currentDay.startOf('week');
      const endOfWeek = currentDay.endOf('week');
      return [formatDate(startOfWeek), formatDate(endOfWeek)];
    } 
    else if (mode === 'month') {
      // Calculate start and end of month
      const startOfMonth = currentDay.startOf('month');
      const endOfMonth = currentDay.endOf('month');
      return [formatDate(startOfMonth), formatDate(endOfMonth)];
    }
    
    // Default to today if no valid mode
    const today = formatDate(currentDay);
    return [today, today];
  };
  
  // Generate appropriate display text based on mode
  const getDisplayText = (mode, date) => {
    if (!date) date = new Date();
    
    const currentDay = dayjs(date);
    
    if (mode === 'day') {
      return formatDisplayDate(currentDay);
    } 
    else if (mode === 'week') {
      const startOfWeek = currentDay.startOf('week');
      const endOfWeek = currentDay.endOf('week');
      return `${startOfWeek.format('DD MMM')} - ${endOfWeek.format('DD MMM YYYY')}`;
    } 
    else if (mode === 'month') {
      return currentDay.format('MMMM YYYY');
    }
    
    return formatDisplayDate(currentDay);
  };
  
  // Handle mode changes (Day/Week/Month)
  const handleModeChange = async (mode) => {
    // Get current date (or today if not available)
    const baseDate = currentDate || new Date();
    
    // Calculate the date range for this mode
    const dateRange = getDateRange(mode, baseDate);
    const displayText = getDisplayText(mode, baseDate);
    
    // Reset custom range flag
    if (onCustomRangeChange) {
      onCustomRangeChange(false);
    }
    
    // Fetch data from backend before updating parent component
    if (onDataLoaded) {
      await fetchDataForDateRange(dateRange);
    }
    
    // Update the parent component
    if (typeof onDateRangeChange === 'function') {
      onDateRangeChange(dateRange, mode, displayText, baseDate);
    } else {
      console.warn('onDateRangeChange is not a function or not provided');
      // Try to use onRangeChange as a fallback
      if (typeof onRangeChange === 'function') {
        onRangeChange(dateRange, mode, displayText, baseDate);
      }
    }
    
    setVisible(false);
    setShowCustomRangePicker(false);
  };
  
  // Handle Today button click - set to current day
  const handleTodayClick = async () => {
    const today = new Date();
    const dateRange = getDateRange('day', today);
    const displayText = getDisplayText('day', today);
    
    // Reset custom range flag
    if (onCustomRangeChange) {
      onCustomRangeChange(false);
    }
    
    // Fetch data from backend before updating parent component
    if (onDataLoaded) {
      await fetchDataForDateRange(dateRange);
    }
    
    // Update the parent component to day mode with today's date
    onDateRangeChange(dateRange, 'day', displayText, today);
  };
  
  // Handle custom date range selection
  const handleCustomRangeChange = async (dates) => {
    if (!dates || dates.length !== 2) return;
    
    const [start, end] = dates;
    const dateRange = [formatDate(start), formatDate(end)];
    const displayText = `${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}`;
    
    // Set custom range flag to true
    if (onCustomRangeChange) {
      onCustomRangeChange(true, dateRange);
    }
    
    // Fetch data from backend before updating parent component
    if (onDataLoaded) {
      await fetchDataForDateRange(dateRange);
    }
    
    // Update parent component
    onDateRangeChange(dateRange, 'custom', displayText);
    
    // Close the calendar and dropdown immediately after selection is complete.
    closeCustomRangePicker();
  };

  // Handle clicks on custom range option
  const handleShowCustomRange = (e) => {
    // Prevent event bubbling which could close the dropdown
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }

    if (closePickerTimerRef.current) {
      clearTimeout(closePickerTimerRef.current);
      closePickerTimerRef.current = null;
    }

    setShowCustomRangePicker(true);
    // Force the range picker to open after a small delay
    setTimeout(() => {
      setRangePickerOpen(true);
    }, 100);
  };

  // Handle clicks on the range picker itself
  const handleRangePickerClick = (e) => {
    // Prevent event bubbling which could close the dropdown
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
  };

  // Custom handler for when the RangePicker calendar panel changes visibility
  const handleCalendarOpenChange = (open) => {
    setRangePickerOpen(open);
    if (!open && showCustomRangePicker) {
      closeCustomRangePicker();
    }
  };

  const handleDropdownVisibleChange = (nextVisible) => {
    if (!nextVisible && (showCustomRangePicker || rangePickerOpen)) {
      closeCustomRangePicker();
      return;
    }

    setVisible(nextVisible);
  };

  // Create the main menu
  const menuItems = [
    <Menu.Item key="day" onClick={() => handleModeChange('day')}>
      Single Day
    </Menu.Item>,
    <Menu.Item key="week" onClick={() => handleModeChange('week')}>
      This Week
    </Menu.Item>,
    <Menu.Item key="month" onClick={() => handleModeChange('month')}>
      This Month
    </Menu.Item>,
    <Menu.Divider key="divider" />,
    <Menu.Item key="custom" onClick={handleShowCustomRange}>
      Custom Range
    </Menu.Item>
  ];

  // Create dropdown overlay content
  const dropdownContent = (
    <div ref={dropdownContentRef} className="custom-dropdown-container" style={{ minWidth: '250px' }}>
      <Menu>
        {menuItems}
      </Menu>
      
      {/* Separate container for range picker to prevent menu issues */}
      {showCustomRangePicker && (
        <div 
          className="p-4 bg-white border-t" 
          onClick={handleRangePickerClick}
          ref={rangePickerRef}
          style={{ position: 'relative', zIndex: 1100 }}
        >
          <RangePicker
            ref={rangePickerRef}
            onChange={handleCustomRangeChange}
            className="w-full"
            format="DD MMM YYYY"
            open={rangePickerOpen}
            onOpenChange={handleCalendarOpenChange}
            getPopupContainer={(trigger) => document.body} // Attach to body for better positioning
            popupClassName="golden-date-range-popup"
            style={{ width: '100%' }}
            popupStyle={{ 
              position: 'absolute', 
              zIndex: 1200,
              boxShadow: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08)'
            }}
          />
        </div>
      )}
    </div>
  );
  
  // Determine if navigation buttons should be disabled
  const navButtonsDisabled = isCustomRange || loading;
  
  // Determine if the current date is today
  const isToday = () => {
    if (!currentDate) return true; // Default to true if no date is set
    
    const today = new Date();
    const current = new Date(currentDate);
    
    return (
      today.getDate() === current.getDate() &&
      today.getMonth() === current.getMonth() &&
      today.getFullYear() === current.getFullYear()
    );
  };

  return (
    <div className="flex w-full flex-col gap-2 bg-white p-2 rounded border sm:flex-row sm:items-center">
      <div className="flex w-full min-w-0 items-center gap-1 sm:w-auto">
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={() => handleNavigation('prev')}
          disabled={navButtonsDisabled}
          className="shrink-0"
        />

        <Dropdown
          overlay={dropdownContent}
          visible={visible}
          onVisibleChange={handleDropdownVisibleChange}
          trigger={['click']}
          disabled={loading}
          placement="bottomLeft"
          overlayStyle={{ minWidth: '250px', zIndex: 1050 }}
        >
          <Button type="link" className="flex min-w-0 flex-1 items-center justify-center px-1 sm:min-w-[190px] sm:px-3">
            {loading ? <LoadingOutlined className="mr-2 shrink-0" /> : <CalendarOutlined className="mr-2 shrink-0" />}
            <span className="font-medium truncate">{dateDisplayText || formatDisplayDate(currentDate || new Date())}</span>
            <DownOutlined className="ml-2 shrink-0" />
          </Button>
        </Dropdown>

        <Button
          type="text"
          icon={<RightOutlined />}
          onClick={() => handleNavigation('next')}
          disabled={navButtonsDisabled}
          className="shrink-0"
        />
      </div>
      
      <div className="grid w-full grid-cols-3 gap-1 sm:ml-2 sm:flex sm:w-auto sm:border-l sm:pl-2">
        {/* Only show Today button if not already on today's date */}
        {!isToday() && (
          <Tooltip title="Jump to today">
            <Button
              type="primary"
              size="small"
              icon={<FieldTimeOutlined />}
              onClick={handleTodayClick}
              className="bg-green-600 col-span-3 sm:col-span-1"
              disabled={loading}
            >
              Today
            </Button>
          </Tooltip>
        )}
        
        <Button
          type={dateViewMode === 'day' ? 'primary' : 'default'}
          size="small"
          onClick={() => handleModeChange('day')}
          className={dateViewMode === 'day' ? 'bg-golden-navy' : ''}
          disabled={loading}
        >
          Day
        </Button>
        
        <Button
          type={dateViewMode === 'week' ? 'primary' : 'default'}
          size="small"
          onClick={() => handleModeChange('week')}
          className={dateViewMode === 'week' ? 'bg-golden-navy' : ''}
          disabled={loading}
        >
          Week
        </Button>
        
        <Button
          type={dateViewMode === 'month' ? 'primary' : 'default'}
          size="small"
          onClick={() => handleModeChange('month')}
          className={dateViewMode === 'month' ? 'bg-golden-navy' : ''}
          disabled={loading}
        >
          Month
        </Button>
      </div>
    </div>
  );
};

export default DateRangeSelector;
